import { useState, useMemo, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, ReferenceLine,
} from "recharts";

const LIGHT = {
  bg: "#EFEFEF", bgCard: "#E4E0D6", black: "#0A0B0D",
  orange: "#E67E50", green: "#5F7F74",
  muted: "#4A4A46", faint: "#D4D0C6", text: "#0A0B0D",
};
const DARK = {
  bg: "#0A0B0D", bgCard: "#1E1E1B", black: "#F5F1E8",
  orange: "#E67E50", green: "#5F7F74",
  muted: "#7A7A75", faint: "#232320", text: "#F5F1E8",
};

const mono = "'IBM Plex Mono', monospace";
const sans = "'DM Sans', sans-serif";

const FONTS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #D4D0C6; }
`;

const TREND_2025 = "#8AACC8";
const TREND_2026 = "#4A80A8";
const MONTHS_ORDER = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const PASSWORD     = process.env.REACT_APP_DASHBOARD_PASSWORD;
const AUTH_KEY     = "ikifi_bdc_auth";

// ─────────────────────────────────────────────────────────────────
// PASSWORD GATE
// ─────────────────────────────────────────────────────────────────
function PasswordGate({ onUnlock }) {
  const [input, setInput] = useState("");
  const [show, setShow]   = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = () => {
    if (input.trim() === PASSWORD.trim()) {
      localStorage.setItem(AUTH_KEY, "true");
      onUnlock();
    } else {
      setError(true);
      setInput("");
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div style={{ background: "#0A0B0D", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 32, padding: "40px 20px", animation: "fadeIn 0.5s ease-in" }}>
      <style>{FONTS}</style>
      <div style={{ display: "flex", alignItems: "baseline" }}>
        <span style={{ fontFamily: sans, fontWeight: 700, fontSize: 42, color: "#F5F1E8", letterSpacing: "-0.02em" }}>ikifi</span>
        <span style={{ fontFamily: sans, fontWeight: 700, fontSize: 48, color: "#E67E50" }}>.</span>
      </div>
      <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.14em", color: "#7A7A75", textTransform: "uppercase", textAlign: "center" }}>Basil Design Co. · NOB Dashboard</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", maxWidth: 300 }}>
        <div style={{ display: "flex", border: `1px solid ${error ? "#E67E50" : "#232320"}`, transition: "border-color 0.2s" }}>
          <input
            type={show ? "text" : "password"}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSubmit()}
            placeholder="Password"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "12px 16px", fontFamily: mono, fontSize: 11, color: "#F5F1E8", letterSpacing: "0.06em" }}
          />
          <button onClick={() => setShow(s => !s)} style={{ background: "transparent", border: "none", cursor: "pointer", padding: "0 14px", fontFamily: mono, fontSize: 11, color: "#7A7A75", letterSpacing: "0.08em" }}>
            {show ? "hide" : "show"}
          </button>
        </div>
        {error && <span style={{ fontFamily: mono, fontSize: 11, color: "#E67E50", letterSpacing: "0.08em", textAlign: "center" }}>Incorrect password</span>}
        <button onClick={handleSubmit} style={{ background: "#E67E50", border: "none", padding: "12px", fontFamily: mono, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#fff", cursor: "pointer" }}>
          Enter
        </button>
      </div>
      <span style={{ fontFamily: mono, fontSize: 11, color: "#7A7A75", letterSpacing: "0.1em", position: "absolute", bottom: 24 }}>Clarity, by design.</span>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{ background: "#0A0B0D", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{FONTS}</style>
      <span style={{ fontFamily: mono, fontSize: 12, color: "#7A7A75", letterSpacing: "0.15em" }}>Loading Basil Design Co. data...</span>
    </div>
  );
}

const SHEET_ID = "1zKD9skpC1BvAuRvrc2OH2lp65QVCSqMUTUUq5tlFD60";
const API_KEY  = process.env.REACT_APP_SHEETS_API_KEY;
const BASE_URL = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values`;

function parseRows(values, filterEmpty = true) {
  if (!values || values.length < 2) return [];
  return values.slice(1)
    .filter(row => row[0] && row[0].trim() !== "")
    .filter(row => !filterEmpty || row.slice(1).some(v => parseFloat(v) !== 0))
    .map(row => ({
      month:               (row[0] || "").slice(0, 3),
      net_income:          parseFloat(row[1]) || 0,
      distributions:       parseFloat(row[2]) || 0,
      accountable_plan:    parseFloat(row[3]) || 0,
      total_owner_benefit: parseFloat(row[4]) || 0,
    }));
}

function fmtK(v) {
  const n = typeof v === "number" ? v : parseFloat(v) || 0;
  if (n === 0) return "—";
  const abs = Math.abs(n);
  const s = abs >= 1000 ? `$${(abs / 1000).toFixed(1)}K` : `$${abs.toFixed(0)}`;
  return n < 0 ? `−${s}` : s;
}

function fmtFull(v) {
  const n = typeof v === "number" ? v : parseFloat(v) || 0;
  const abs = Math.abs(n);
  const s = `$${abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n < 0 ? `−${s}` : s;
}

function pct(a, b) {
  if (!b) return null;
  return ((a - b) / Math.abs(b)) * 100;
}

function topOfHourLabel() {
  const now = new Date();
  const top = new Date(now);
  top.setMinutes(0, 0, 0);
  return top.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function Logo({ t, size = 19 }) {
  return (
    <a href="https://www.ikifi.co" target="_blank" rel="noreferrer"
      style={{ display: "flex", alignItems: "baseline", textDecoration: "none" }}>
      <span style={{ fontFamily: sans, fontWeight: 700, fontSize: `${size}px`, color: t.black, letterSpacing: "-0.02em" }}>ikifi</span>
      <span style={{ fontFamily: sans, fontWeight: 700, fontSize: `${size * 1.15}px`, color: t.orange }}>.</span>
    </a>
  );
}

function DarkToggle({ dark, setDark, t }) {
  return (
    <button onClick={() => setDark(d => !d)} style={{ width: 38, height: 20, background: dark ? t.orange : t.faint, border: "none", cursor: "pointer", position: "relative", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: 3, width: 14, height: 14, left: dark ? 21 : 3, transition: "left 0.2s", background: dark ? "#fff" : t.black }} />
    </button>
  );
}

function SLabel({ children, t }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
      <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.14em", color: t.muted, textTransform: "uppercase", whiteSpace: "nowrap" }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: t.faint }} />
    </div>
  );
}

function InfoTooltip({ text, t }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: 5 }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span style={{ fontFamily: mono, fontSize: 11, color: "#9B9891", cursor: "default", lineHeight: 1 }}>ⓘ</span>
      {show && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", background: t.bgCard, border: `1px solid ${t.faint}`, padding: "8px 12px", fontFamily: mono, fontSize: 11, color: t.muted, whiteSpace: "nowrap", pointerEvents: "none", zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}>
          <div style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderBottom: `5px solid ${t.faint}` }} />
          {text}
        </div>
      )}
    </div>
  );
}

function KPICard({ label, value, sub, positive, t }) {
  const [hov, setHov] = useState(false);
  const barCol = positive === true ? t.green : positive === false ? t.orange : t.faint;
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ flex: 1, minWidth: 0, background: t.bgCard, display: "flex", flexDirection: "column",
        transform: hov ? "translateY(-2px)" : "none", boxShadow: hov ? "0 4px 12px rgba(0,0,0,0.08)" : "none",
        transition: "transform 0.15s, box-shadow 0.15s", cursor: "default" }}>
      <div style={{ height: 3, background: barCol }} />
      <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.13em", textTransform: "uppercase", color: t.muted }}>{label}</div>
        <div style={{ fontFamily: mono, fontSize: 22, fontWeight: 500, color: positive === false ? t.orange : t.black, letterSpacing: "-0.03em", lineHeight: 1 }}>{value}</div>
        <div style={{ fontFamily: mono, fontSize: 11, color: t.muted, minHeight: 12 }}>{sub || ""}</div>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label, dark }) {
  const t = dark ? DARK : LIGHT;
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.faint}`, padding: "12px 16px", fontFamily: mono, fontSize: 11, lineHeight: 1.9, minWidth: 200 }}>
      <div style={{ fontSize: 12, color: t.black, marginBottom: 8, borderBottom: `1px solid ${t.faint}`, paddingBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
          <span style={{ color: p.color || t.muted }}>{p.name}</span>
          <span style={{ color: p.value < 0 ? t.orange : t.black }}>{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function NOBDashboard() {
  const [dark, setDark]         = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  const [tab, setTab]           = useState("overview");
  const [year, setYear]         = useState("2026");
  const [hovRow, setHovRow]     = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [data2026, setData2026] = useState([]);
  const [data2025, setData2025] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [unlocked, setUnlocked]       = useState(false);
  const [gateLoading, setGateLoading] = useState(false);

  const t          = dark ? DARK : LIGHT;
  const months2026 = data2026.map(r => r.month);
  const activeData = year === "2026" ? data2026 : data2025.filter(r => months2026.includes(r.month));
  const ytdLabel   = year === "2026" ? "YTD" : "Last YTD";

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  useEffect(() => {
    if (localStorage.getItem(AUTH_KEY) === "true") {
      setGateLoading(true);
      setTimeout(() => { setUnlocked(true); setGateLoading(false); }, 2000);
    }
  }, []);

  const handleUnlock = () => {
    setGateLoading(true);
    setTimeout(() => { setUnlocked(true); setGateLoading(false); }, 2000);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const [res26, res25] = await Promise.all([
          fetch(`${BASE_URL}/Clean_NOB_Transactions_YTD!A1:E13?key=${API_KEY}`),
          fetch(`${BASE_URL}/Clean_NOB_Transactions_Last_Year!A1:E13?key=${API_KEY}`),
        ]);
        const [json26, json25] = await Promise.all([res26.json(), res25.json()]);
        if (json26.values) setData2026(parseRows(json26.values, true));
        if (json25.values) setData2025(parseRows(json25.values, false));
      } catch (err) {
        setError("Could not load sheet data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const refreshTime = useMemo(() => topOfHourLabel(), []);

  const ytdNI      = activeData.reduce((s, r) => s + r.net_income, 0);
  const ytdDist    = activeData.reduce((s, r) => s + r.distributions, 0);
  const ytdTOB     = activeData.reduce((s, r) => s + r.total_owner_benefit, 0);
  const prior2025  = data2025.filter(r => months2026.includes(r.month));
  const ytdTOB2025 = prior2025.reduce((s, r) => s + r.total_owner_benefit, 0);
  const yoyPct     = ytdTOB2025 > 0 ? pct(data2026.reduce((s, r) => s + r.total_owner_benefit, 0), ytdTOB2025) : null;
  const yoyLabel   = yoyPct != null ? `${yoyPct >= 0 ? "+" : ""}${yoyPct.toFixed(1)}%` : "—";

  const healthData = activeData.map(r => ({
    month: r.month,
    "Net Income":    r.net_income,
    "Distributions": r.distributions,
  }));

  const trendData = useMemo(() => {
    const rows = [];
    const months2026Set = new Set(data2026.map(r => r.month));
    data2025.filter(r => months2026Set.has(r.month)).forEach(r =>
      rows.push({ label: `${r.month} '25`, year: 2025, nob: r.total_owner_benefit })
    );
    data2026.forEach(r =>
      rows.push({ label: `${r.month} '26`, year: 2026, nob: r.total_owner_benefit })
    );
    return rows;
  }, [data2025, data2026]);

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "trend",    label: "Rolling Trend" },
  ];

  const TABLE_HEADERS = [
    { label: "Month",               w: "10%" },
    { label: "Net Income",          w: "22%" },
    { label: "Distributions",       w: "22%" },
    { label: "Accountable Plan",    w: "22%" },
    { label: "Total Owner Benefit", w: "24%" },
  ];

  const tableRows = activeData.map((r, i) => {
    const key = `row-${i}`;
    return (
      <div key={key}
        onMouseEnter={() => setHovRow(key)}
        onMouseLeave={() => setHovRow(null)}
        style={{ display: "flex", alignItems: "center", padding: "11px 16px",
          borderBottom: `1px solid ${t.faint}`,
          borderLeft: `4px solid ${r.net_income < 0 ? t.orange : t.green}`,
          background: hovRow === key ? `${t.orange}08` : i % 2 === 0 ? t.bg : "transparent",
          transition: "background 0.15s" }}>
        <div style={{ width: "10%", flexShrink: 0, fontFamily: sans,  fontSize: 13, fontWeight: 600, color: t.black }}>{r.month}</div>
        <div style={{ width: "22%", flexShrink: 0, fontFamily: mono, fontSize: 12, color: r.net_income < 0 ? t.orange : t.black }}>{fmtFull(r.net_income)}</div>
        <div style={{ width: "22%", flexShrink: 0, fontFamily: mono, fontSize: 12, color: t.black }}>{fmtFull(r.distributions)}</div>
        <div style={{ width: "22%", flexShrink: 0, fontFamily: mono, fontSize: 12, color: t.black }}>{r.accountable_plan > 0 ? fmtFull(r.accountable_plan) : "—"}</div>
        <div style={{ width: "24%", flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 500, color: t.green }}>{fmtFull(r.total_owner_benefit)}</div>
      </div>
    );
  });

  const tableFooter = (
    <div style={{ display: "flex", padding: "13px 16px" }}>
      <div style={{ width: "10%", flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 700, color: t.black, textTransform: "uppercase", letterSpacing: "0.1em" }}>{ytdLabel}</div>
      <div style={{ width: "22%", flexShrink: 0, fontFamily: mono, fontSize: 13, fontWeight: 700, color: ytdNI < 0 ? t.orange : t.black }}>{fmtFull(ytdNI)}</div>
      <div style={{ width: "22%", flexShrink: 0, fontFamily: mono, fontSize: 13, fontWeight: 700, color: t.black }}>{fmtFull(ytdDist)}</div>
      <div style={{ width: "22%", flexShrink: 0 }} />
      <div style={{ width: "24%", flexShrink: 0, fontFamily: mono, fontSize: 13, fontWeight: 700, color: t.green }}>{fmtFull(ytdTOB)}</div>
    </div>
  );

  if (!unlocked && !gateLoading) return <PasswordGate onUnlock={handleUnlock} />;
  if (gateLoading) return <LoadingScreen />;

  if (loading) return (
    <div style={{ background: t.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{FONTS}</style>
      <span style={{ fontFamily: mono, fontSize: 11, color: t.muted, letterSpacing: "0.1em" }}>Loading…</span>
    </div>
  );

  if (error) return (
    <div style={{ background: t.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{FONTS}</style>
      <span style={{ fontFamily: mono, fontSize: 11, color: t.orange, letterSpacing: "0.1em" }}>{error}</span>
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ background: t.bg, minHeight: "100vh", fontFamily: sans, animation: "fadeIn 0.6s ease-in" }}>
        <style>{FONTS}</style>
        <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${t.faint}`, background: t.bg }}>
          <Logo t={t} size={22} />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
              <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 500, color: t.black, letterSpacing: "0.04em" }}>NOB | Basil Design Co.</span>
              <span style={{ fontFamily: mono, fontSize: 11, color: t.muted }}>Last refreshed {refreshTime}</span>
            </div>
            <DarkToggle dark={dark} setDark={setDark} t={t} />
          </div>
        </nav>
        <div style={{ padding: "24px 20px 48px", display: "flex", flexDirection: "column", gap: 2 }}>

          {/* YEAR TOGGLE — mobile */}
          <div style={{ display: "flex", gap: 2, marginBottom: 16 }}>
            {[{ val: "2026", display: "2026 YTD" }, { val: "2025", display: "2025 Last YTD" }].map(({ val, display }) => (
              <button key={val} onClick={() => setYear(val)} style={{ flex: 1, fontFamily: mono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", padding: "8px", cursor: "pointer", transition: "all 0.15s", background: year === val ? t.black : "transparent", color: year === val ? (dark ? t.bg : "#fff") : t.muted, border: `1px solid ${year === val ? t.black : t.faint}` }}>{display}</button>
            ))}
          </div>

          {[
            { label: `Total Owner Benefit ${ytdLabel}`, value: fmtK(ytdTOB),  sub: "distributions + accountable plan", positive: ytdTOB > 0 },
            { label: `Net Income ${ytdLabel}`,          value: fmtK(ytdNI),   sub: "actual net income",                positive: ytdNI > 0 },
            { label: `Distributions ${ytdLabel}`,       value: fmtK(ytdDist), sub: "cash taken out",                   positive: true },
            ...(year === "2026" ? [{ label: "Year-Over-Year Owner Benefit", value: yoyLabel, sub: "vs same months 2025", positive: yoyPct != null ? yoyPct >= 0 : undefined }] : []),
          ].map((card, i) => (
            <div key={i} style={{ background: t.bgCard, display: "flex", flexDirection: "column" }}>
              <div style={{ height: 3, background: card.positive === true ? t.green : card.positive === false ? t.orange : t.faint }} />
              <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.13em", textTransform: "uppercase", color: t.muted }}>{card.label}</div>
                <div style={{ fontFamily: mono, fontSize: 28, fontWeight: 500, color: card.positive === false ? t.orange : t.black, letterSpacing: "-0.03em", lineHeight: 1 }}>{card.value}</div>
                <div style={{ fontFamily: mono, fontSize: 11, color: t.muted }}>{card.sub}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: "0 20px 32px", borderTop: `1px solid ${t.faint}`, paddingTop: 20, display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.muted }}>© ikifi 2026</span>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.muted }}>Clarity, by design.</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: sans, animation: "fadeIn 0.5s ease-in", overflowX: "hidden" }}>
      <style>{FONTS}</style>

      <nav style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", padding: "20px 48px", borderBottom: `1px solid ${t.faint}`, background: t.bg }}>
        <Logo t={t} size={26} />
        <div style={{ display: "flex", gap: 8 }}>
          {TABS.map(({ id, label }) => (
            <button key={id} onClick={() => setTab(id)} style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", padding: "8px 20px", cursor: "pointer", transition: "all 0.15s", background: tab === id ? t.orange : "transparent", color: tab === id ? "#fff" : t.muted, border: `1px solid ${tab === id ? t.orange : t.faint}` }}>{label}</button>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
            <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 500, color: t.black, letterSpacing: "0.04em" }}>NOB | Basil Design Co.</span>
            <span style={{ fontFamily: mono, fontSize: 11, color: t.muted, letterSpacing: "0.06em" }}>Last refreshed {refreshTime}</span>
          </div>
          <DarkToggle dark={dark} setDark={setDark} t={t} />
          <button onClick={() => { localStorage.removeItem(AUTH_KEY); setUnlocked(false); }} style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: t.muted, background: "transparent", border: `1px solid ${t.faint}`, cursor: "pointer", padding: "8px 20px", transition: "all 0.15s" }}>Sign Out</button>
        </div>
      </nav>

      <div style={{ padding: "40px 48px 64px" }}>

        {tab === "overview" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 28 }}>
              {[{ val: "2026", display: "2026 YTD" }, { val: "2025", display: "2025 Last YTD" }].map(({ val, display }) => (
                <button key={val} onClick={() => setYear(val)} style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", padding: "7px 18px", cursor: "pointer", transition: "all 0.15s", background: year === val ? t.black : "transparent", color: year === val ? (dark ? t.bg : "#fff") : t.muted, border: `1px solid ${year === val ? t.black : t.faint}` }}>{display}</button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 2, marginBottom: 48 }}>
              <KPICard t={t} label={`Total Owner Benefit ${ytdLabel}`}  value={fmtK(ytdTOB)}  sub="distributions + accountable plan" positive={ytdTOB > 0} />
              <KPICard t={t} label={`Net Income ${ytdLabel}`}           value={fmtK(ytdNI)}   sub="actual net income"                positive={ytdNI > 0} />
              <KPICard t={t} label={`Distributions ${ytdLabel}`}        value={fmtK(ytdDist)} sub="cash taken out"                   positive />
              {year === "2026" && <KPICard t={t} label="Year-Over-Year Owner Benefit" value={yoyLabel} sub="vs same months 2025" positive={yoyPct != null ? yoyPct >= 0 : undefined} />}
            </div>

            <SLabel t={t}>Distribution Health · {year === "2026" ? "YTD 2026" : "Last YTD 2025"}</SLabel>
            <div style={{ display: "flex", gap: 20, marginBottom: 16 }}>
              {[{ color: t.green, label: "Net Income" }, { color: t.orange, label: "Distributions" }].map(l => (
                <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: mono, fontSize: 11, color: t.muted, letterSpacing: "0.08em" }}>
                  <div style={{ width: 10, height: 10, background: l.color }} />
                  {l.label}
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={healthData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }} barGap={6} barCategoryGap="35%">
                <CartesianGrid strokeDasharray="3 3" stroke={t.faint} vertical={false} />
                <ReferenceLine y={0} stroke={t.muted} />
                <XAxis dataKey="month" tick={{ fill: t.muted, fontSize: 11, fontFamily: mono }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => fmtK(v)} tick={{ fill: t.muted, fontSize: 12, fontFamily: mono }} axisLine={false} tickLine={false} width={60} />
                <Tooltip content={<ChartTooltip dark={dark} />} />
                <Bar dataKey="Net Income"    fill={t.green}  radius={[2,2,0,0]} barSize={48} />
                <Bar dataKey="Distributions" fill={t.orange} radius={[2,2,0,0]} barSize={48} />
              </BarChart>
            </ResponsiveContainer>

            <div style={{ height: 40 }} />

            <SLabel t={t}>Monthly Detail · {year === "2026" ? "YTD 2026" : "Last YTD 2025"}</SLabel>
            <div style={{ border: `1px solid ${t.faint}` }}>
              <div style={{ display: "flex", padding: "10px 16px", background: dark ? "#1E1E1C" : t.black, borderBottom: `1px solid ${t.faint}` }}>
                {TABLE_HEADERS.map((h, i) => (
                  <div key={i} style={{ width: h.w, flexShrink: 0, display: "flex", alignItems: "center", fontFamily: mono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9B9891" }}>
                    {h.label}
                    {h.label === "Net Income" && <InfoTooltip text="Net income dips at quarter-end due to payroll expense" t={t} />}
                  </div>
                ))}
              </div>
              <div>{tableRows}</div>
              <div style={{ borderTop: `1px solid ${t.faint}`, background: t.bgCard }}>{tableFooter}</div>
            </div>
          </>
        )}

        {tab === "trend" && (
          <>
            <SLabel t={t}>Owner Benefit · Rolling Trend</SLabel>
            <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
              {[{ color: TREND_2025, label: "2025" }, { color: TREND_2026, label: "2026" }].map(l => (
                <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: mono, fontSize: 11, color: t.muted, letterSpacing: "0.08em" }}>
                  <div style={{ width: 24, height: 2, background: l.color }} />
                  {l.label}
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={t.faint} vertical={false} />
                <ReferenceLine y={0} stroke={t.muted} />
                <XAxis dataKey="label" type="category" allowDuplicatedCategory={false} tick={{ fill: t.muted, fontSize: 12, fontFamily: mono }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => fmtK(v)} tick={{ fill: t.muted, fontSize: 12, fontFamily: mono }} axisLine={false} tickLine={false} width={60} />
                <Tooltip content={<ChartTooltip dark={dark} />} />
                {[2025, 2026].map((yr, i) => (
                  <Line key={yr} data={trendData.filter(d => d.year === yr)} type="monotone" dataKey="nob" name={String(yr)}
                    stroke={i === 0 ? TREND_2025 : TREND_2026} strokeWidth={2}
                    dot={{ fill: i === 0 ? TREND_2025 : TREND_2026, r: 4, strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 0 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>

            <div style={{ height: 40 }} />

            <SLabel t={t}>Month by Month · 2025 vs 2026</SLabel>
            <div style={{ border: `1px solid ${t.faint}`, overflow: "hidden" }}>
              <div style={{ display: "flex", padding: "10px 16px", background: dark ? "#1E1E1C" : t.black, borderBottom: `1px solid ${t.faint}` }}>
                {[
                  { label: "Month",              w: "16%" },
                  { label: "2026 Owner Benefit", w: "21%" },
                  { label: "2025 Owner Benefit", w: "21%" },
                  { label: "Dollar Change",      w: "21%" },
                  { label: "Percent Change",     w: "21%" },
                ].map((h, i) => (
                  <div key={i} style={{ width: h.w, flexShrink: 0, fontFamily: mono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9B9891" }}>{h.label}</div>
                ))}
              </div>
              <div>
                {MONTHS_ORDER.filter(m => data2026.find(r => r.month === m)).map((m, i) => {
                  const r26  = data2026.find(r => r.month === m);
                  const r25  = data2025.find(r => r.month === m);
                  const n26  = r26?.total_owner_benefit ?? 0;
                  const n25  = r25?.total_owner_benefit ?? null;
                  const diff = n25 != null ? n26 - n25 : null;
                  const p    = n25 != null && n25 > 0 ? pct(n26, n25) : null;
                  const lb   = diff == null ? t.faint : diff < 0 ? t.orange : t.green;
                  const key  = `yoy-${i}`;
                  return (
                    <div key={key} onMouseEnter={() => setHovRow(key)} onMouseLeave={() => setHovRow(null)}
                      style={{ display: "flex", alignItems: "center", padding: "11px 16px", borderBottom: `1px solid ${t.faint}`, borderLeft: `4px solid ${lb}`, background: hovRow === key ? `${t.orange}08` : i % 2 === 0 ? t.bg : "transparent", transition: "background 0.15s" }}>
                      <div style={{ width: "16%", flexShrink: 0, fontFamily: sans,  fontSize: 13, fontWeight: 600, color: t.black }}>{m}</div>
                      <div style={{ width: "21%", flexShrink: 0, fontFamily: mono, fontSize: 12, color: t.black }}>{fmtFull(n26)}</div>
                      <div style={{ width: "21%", flexShrink: 0, fontFamily: mono, fontSize: 12, color: t.muted }}>{n25 != null ? fmtFull(n25) : "—"}</div>
                      <div style={{ width: "21%", flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 500, color: diff == null ? t.muted : diff < 0 ? t.orange : t.green }}>{diff != null ? fmtFull(diff) : "—"}</div>
                      <div style={{ width: "21%", flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 500, color: p == null ? t.muted : p < 0 ? t.orange : t.green }}>{p != null ? `${p >= 0 ? "+" : ""}${p.toFixed(1)}%` : "—"}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <div style={{ marginTop: 60, paddingTop: 20, borderTop: `1px solid ${t.faint}`, display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.muted }}>© ikifi 2026</span>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.muted }}>Clarity, by design.</span>
        </div>
      </div>
    </div>
  );
}